// assets/cbh-main-article.js
// CBH — Sticky Article Toolbar controller
// - Toggles .is-visible once the title has scrolled past
// - Updates --cbh-read-progress (0..1) for the progress bar
(() => {
  'use strict';

  const SELECTORS = {
    toolbar: '.article__toolbar',
    content: '[data-cbh-article-rte]',
    title: '.article-template__title',
  };

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  const state = {
    bound: false,
    raf: 0,
    lastY: 0,
    toolbar: null,
    content: null,
    title: null,
    startY: 0,
    endY: 0,
    triggerY: 0,
  };

  function refreshElements() {
    state.toolbar = document.querySelector(SELECTORS.toolbar);
    state.content = document.querySelector(SELECTORS.content);
    state.title = document.querySelector(SELECTORS.title);
  }

  function measure() {
    // If we can’t find required elements, fail safely
    if (!state.toolbar || !state.content) {
      return;
    }

    const contentRect = state.content.getBoundingClientRect();
    const contentTop = contentRect.top + window.scrollY;
    const contentBottom = contentRect.bottom + window.scrollY;

    // Progress starts at the top of the RTE; ends near the bottom (accounting for viewport height)
    state.startY = contentTop - 16;
    state.endY = contentBottom - window.innerHeight + 16;
    if (state.endY < state.startY) state.endY = state.startY;

    if (state.title) {
      const titleRect = state.title.getBoundingClientRect();
      state.triggerY = titleRect.bottom + window.scrollY;
    } else {
      // Fallback if the title selector changes
      state.triggerY = state.startY + 200;
    }
  }

  function setProgress(progress01) {
    if (!state.toolbar) return;
    state.toolbar.style.setProperty('--cbh-read-progress', String(progress01));
  }

  function setVisible(visible) {
    if (!state.toolbar) return;
    state.toolbar.classList.toggle('is-visible', !!visible);
  }

  function update() {
    state.raf = 0;

    refreshElements();

    // If not on an article layout (or markup changed), just bail quietly
    if (!state.toolbar || !state.content) return;

    measure();

    const y = state.lastY;

    // Show once we’ve scrolled past the title area
    const shouldShow = y > (state.triggerY + 40);
    setVisible(shouldShow);

    // Progress calculation
    let p = 0;
    if (y <= state.startY) p = 0;
    else if (y >= state.endY) p = 1;
    else p = (y - state.startY) / (state.endY - state.startY || 1);

    p = clamp(p, 0, 1);

    // Keep it as a short decimal string for CSS calc()
    setProgress(p.toFixed(4));
  }

  function onScroll() {
    state.lastY = window.scrollY || window.pageYOffset || 0;
    if (!state.raf) state.raf = window.requestAnimationFrame(update);
  }

  function bindOnce() {
    if (state.bound) return;
    state.bound = true;

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      // Recompute on resize
      onScroll();
    }, { passive: true });

    // Theme editor: when a section reloads, re-select + re-measure
    document.addEventListener('shopify:section:load', () => {
      onScroll();
    });
  }

  function init() {
    refreshElements();
    bindOnce();
    state.lastY = window.scrollY || window.pageYOffset || 0;
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
